/**
 * Compute the offset (in minutes) of `timeZone` at the given UTC instant.
 * Positive when the zone is behind UTC (e.g. -300 for America/New_York in EST).
 */
export function getZoneOffsetMinutes(timeZone: string, utcMs: number): number {
  // `hourCycle: 'h23'` forces 0-23 -- `hour12: false` historically emits 24
  // at midnight in some engines, which would silently roll the day forward.
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(new Date(utcMs))
  const get = (type: string) =>
    Number(parts.find((p) => p.type === type)?.value)
  const asUtc = Date.UTC(
    get('year'),
    get('month') - 1,
    get('day'),
    get('hour'),
    get('minute'),
    get('second')
  )
  return (asUtc - utcMs) / 60_000
}

/**
 * Combine a wall-clock date + time in `timeZone` into a UTC ISO-8601 string.
 *
 * @param dateStr - `YYYY-MM-DD`
 * @param timeStr - `HH:mm` (24h)
 * @param timeZone - IANA zone name the date/time are expressed in
 * @returns ISO-8601 UTC instant, e.g. `2024-05-01T13:00:00.000Z`
 */
export function zonedDateTimeToUtcIso(
  dateStr: string,
  timeStr: string,
  timeZone: string
): string {
  const [year, month, day] = dateStr.split('-').map(Number)
  const [hour, minute] = timeStr.split(':').map(Number)
  // Treat the wall-clock in `timeZone`. First pass: assume local == UTC.
  const guessUtcMs = Date.UTC(year, month - 1, day, hour, minute)
  const offset = getZoneOffsetMinutes(timeZone, guessUtcMs)
  return new Date(guessUtcMs - offset * 60_000).toISOString()
}

/**
 * Inverse of `zonedDateTimeToUtcIso` — render a UTC ISO-8601 instant as the
 * wall-clock date + time it corresponds to in `timeZone`.
 *
 * @param iso - ISO-8601 UTC instant
 * @param timeZone - IANA zone name to render the instant in
 * @returns `{ date: 'YYYY-MM-DD', time: 'HH:mm' }` in `timeZone`
 */
export function splitZonedIso(
  iso: string,
  timeZone: string
): { date: string; time: string } {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).formatToParts(new Date(iso))
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? ''
  return {
    date: `${get('year')}-${get('month')}-${get('day')}`,
    time: `${get('hour')}:${get('minute')}`,
  }
}
